import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
    { label: "Home", href: "#home", id: "home", testid: "nav-home-link" },
    { label: "How It Works", href: "#how-it-works", id: "how-it-works", testid: "nav-how-it-works-link" },
    { label: "Services", href: "#services", id: "services", testid: "nav-services-link" },
    { label: "About", href: "#about", id: "about", testid: "nav-about-link" },
    { label: "Contact", href: "#contact", id: "contact", testid: "nav-contact-link" },
];

const Nav = ({ onEarlyAccess, onRequestService }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState("home");

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 24);
            let current = "home";
            for (const l of LINKS) {
                const el = document.getElementById(l.id);
                if (el && el.getBoundingClientRect().top <= 120) current = l.id;
            }
            setActive(current);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, href) => {
        e.preventDefault();
        setOpen(false);
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/85 border-b border-slate-100 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.08)]"
                    : "bg-transparent"
            }`}
            data-testid="site-nav"
        >
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
                <a href="#home" onClick={(e) => go(e, "#home")} data-testid="nav-logo" aria-label="Sevano home">
                    <Logo />
                </a>

                <div className="hidden md:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <a
                            key={l.id}
                            href={l.href}
                            data-testid={l.testid}
                            onClick={(e) => go(e, l.href)}
                            className={`nav-link text-sm font-semibold ${
                                active === l.id ? "text-[#0B5CF5] active" : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            {l.label}
                        </a>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <button
                        data-testid="nav-request-service-button"
                        onClick={onRequestService}
                        className="btn-ghost hidden lg:inline-flex items-center px-5 py-2.5 text-sm"
                    >
                        Request a Service
                    </button>
                    <button
                        data-testid="nav-early-access-button"
                        onClick={onEarlyAccess}
                        className="btn-primary hidden sm:inline-flex items-center px-5 py-2.5 text-sm"
                    >
                        Get Early Access
                    </button>
                    <button
                        data-testid="nav-mobile-menu-button"
                        className="md:hidden p-2 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden overflow-hidden backdrop-blur-xl bg-white/95 border-b border-slate-100"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-4 py-4 flex flex-col gap-1">
                            {LINKS.map((l) => (
                                <a
                                    key={l.id}
                                    href={l.href}
                                    data-testid={`mobile-${l.testid}`}
                                    onClick={(e) => go(e, l.href)}
                                    className="px-3 py-3 rounded-xl text-base font-semibold text-slate-700 hover:bg-[#F4F8FF] hover:text-[#0B5CF5] transition-colors"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <button
                                data-testid="mobile-nav-request-service-button"
                                onClick={() => {
                                    setOpen(false);
                                    onRequestService();
                                }}
                                className="btn-ghost mt-2 px-5 py-3 text-sm"
                            >
                                Request a Service
                            </button>
                            <button
                                data-testid="mobile-nav-early-access-button"
                                onClick={() => {
                                    setOpen(false);
                                    onEarlyAccess();
                                }}
                                className="btn-primary mt-2 px-5 py-3 text-sm"
                            >
                                Get Early Access
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Nav;
